/**
 * Doserne — "Consum Jarvis": urmărește costul LLM al lui Jarvis (Chat, Vision,
 * Voce, Transcriere) pentru toți adminii, în NOK (kr). Curs fix 1 USD = 10.8 kr.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Coins, MessageSquare, Eye, Volume2, Mic, RefreshCw, CalendarDays, Users,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { id: "today", label: "Azi" },
  { id: "7d", label: "7 zile" },
  { id: "30d", label: "30 zile" },
  { id: "month", label: "Luna aceasta" },
  { id: "all", label: "Tot" },
];

const KIND_META = {
  chat:    { label: "Chat",        icon: MessageSquare, color: "text-sky-600",     bg: "bg-sky-50" },
  vision:  { label: "Vision",      icon: Eye,           color: "text-violet-600",  bg: "bg-violet-50" },
  tts:     { label: "Voce",        icon: Volume2,       color: "text-emerald-600", bg: "bg-emerald-50" },
  whisper: { label: "Transcriere", icon: Mic,           color: "text-amber-600",   bg: "bg-amber-50" },
};

function kr(n) {
  return `${(Number(n) || 0).toFixed(2)} kr`;
}

function fmtDay(d) {
  try {
    const dt = new Date(`${d}T00:00:00`);
    return dt.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", weekday: "short" });
  } catch { return d; }
}

export default function DoserneJarvisCosts() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const isAdmin = !!(user?.is_admin || user?.role === "platform_admin" || user?.is_doserne_admin);
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/doserne/jarvis/costs?period=${period}`, authHeader());
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, authHeader]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!authLoading && !user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!authLoading && user && !isAdmin) return <Navigate to="/doserne" replace />;

  return (
    <DoserneShell title="Consum Jarvis" subtitle="Cost estimativ LLM (Chat · Vision · Voce · Transcriere)">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="jarvis-costs-page">
        {/* Period selector */}
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              data-testid={`period-${p.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                period === p.id
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="jarvis-costs-refresh"
            className="ml-auto p-2 rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label="Reîncarcă"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : !data ? (
          <div className="text-center py-24 text-slate-400" data-testid="jarvis-costs-error">
            Nu am putut încărca datele de consum.
          </div>
        ) : (
          <>
            {/* Total */}
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6 shadow-lg">
              <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                <Coins className="w-4 h-4" /> Total consum Jarvis
              </div>
              <div className="mt-2 text-4xl sm:text-5xl font-black tracking-tight" data-testid="jarvis-costs-total">
                {kr(data.total_nok)}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                ≈ ${Number(data.total_usd || 0).toFixed(2)} · curs {data.rate_nok_per_usd} kr/USD · toți adminii
              </div>
            </div>

            {/* By kind */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["chat", "vision", "tts", "whisper"].map((k) => {
                const meta = KIND_META[k];
                const Icon = meta.icon;
                const row = (data.by_kind || []).find((x) => x.kind === k);
                return (
                  <div key={k} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`kind-${k}`}>
                    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${meta.bg} ${meta.color} mb-2`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs text-slate-500 font-medium">{meta.label}</div>
                    <div className="text-lg font-bold text-slate-900">{kr(row ? row.cost_nok : 0)}</div>
                  </div>
                );
              })}
            </div>

            {/* By day */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">
                <CalendarDays className="w-4 h-4 text-slate-400" /> Consum pe zi
              </div>
              {(data.by_day || []).length === 0 ? (
                <div className="px-5 py-10 text-center text-slate-400 text-sm">Niciun consum înregistrat în această perioadă.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.by_day.map((row) => (
                    <div key={row.day} className="flex items-center gap-3 px-5 py-3" data-testid={`day-${row.day}`}>
                      <div className="w-28 shrink-0 text-sm text-slate-600">{fmtDay(row.day)}</div>
                      <div className="flex-1 flex flex-wrap gap-1.5">
                        {Object.entries(row.kinds || {}).map(([k, v]) => (
                          <span key={k} className={`text-[11px] px-2 py-0.5 rounded-full ${KIND_META[k]?.bg || "bg-slate-100"} ${KIND_META[k]?.color || "text-slate-600"}`}>
                            {KIND_META[k]?.label || k}: {kr(v)}
                          </span>
                        ))}
                      </div>
                      <div className="text-sm font-bold text-slate-900 tabular-nums">{kr(row.cost_nok)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* By admin */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">
                <Users className="w-4 h-4 text-slate-400" /> Consum pe admin
              </div>
              {(data.by_admin || []).length === 0 ? (
                <div className="px-5 py-10 text-center text-slate-400 text-sm">Niciun consum înregistrat.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.by_admin.map((row) => (
                    <div key={row.admin_email} className="flex items-center justify-between px-5 py-3" data-testid={`admin-${row.admin_email}`}>
                      <div className="text-sm text-slate-600 truncate">{row.admin_email}</div>
                      <div className="text-sm font-bold text-slate-900 tabular-nums">{kr(row.cost_nok)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-[11px] text-slate-400 text-center">
              Costuri estimative pe baza tarifelor publice per token/caracter. Cifrele reale de pe Universal Key pot varia ușor.
            </p>
          </>
        )}
      </div>
    </DoserneShell>
  );
}
